// #region Imports

import * as React from 'react';

// Material-UI
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridOverlay,
  GridRenderCellParams
} from '@mui/x-data-grid';
import FeedbackIcon from '@mui/icons-material/Feedback';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  FeedbackItem
} from '../../models';

import {
  EMPTY_STRING,
  FeedbackView
} from '../../global';

// Responsive hooks
import { useResponsive } from '../../hooks/UseResponsive';
// UTC to local date formatting hooks
import { useDates } from '../../hooks/UseDates';

// #endregion

// #region Interfaces

interface CaldendarGridProps {
  handleViewDetail: (id: number) => void;
  isLoading: boolean;
  rows: Array<FeedbackItem>;
  view: FeedbackView;
}

// #endregion

const FeedbackGrid: React.FC<CaldendarGridProps> = (props) => {

  const { isMobile } = useResponsive();
  const { dateStringToLocalDateFormatted } = useDates();

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      flexGrow: 1,
      padding: theme.spacing(2, 2, 2, 2),
    },
    noRows: {
      padding: theme.spacing(2, 0, 2, 0),
    },
  };

  // #endregion

  // #region Methods

  /*
 * Grid column definitions
 */
  const columns = (): Array<GridColDef> => {

    return [
      {
        field: 'feedbackID',
        headerName: EMPTY_STRING,
        width: 50,
        type: 'number',
        align: 'center',
        renderCell: (params: GridCellParams) => (
          renderView(params)
        )
      }, {
        field: 'reported',
        headerName: 'Reported',
        width: isMobile ? 0 : 200,
        type: 'string',
        // Pass the function reference directly
        renderCell: renderReportedDate
      }, {
        field: 'reportedBy',
        headerName: 'Reported By',
        width: isMobile ? 0 : 150,
        type: 'string'
      }, {
        field: 'feedbackType',
        headerName: 'Type',
        width: isMobile ? 0 : 150,
        type: 'string',
        renderCell: renderType
      }, {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        type: 'string'
      }
    ];

  }

  /*
   * Overlay displayed when no data
   */
  const noRowsOverlay = () => {
    return (
      <GridOverlay>
        <div style={styles.noRows}>No feedback for the selected date range and feedback type</div>
      </GridOverlay>
    );
  }

  /*
   * Reported date (formatted)
   */
  const renderReportedDate = (params: GridRenderCellParams) => {
    const r = params.row.reported;
    return dateStringToLocalDateFormatted(r);
  }

  /*
   * Feedback type
   */
  const renderType = (params: GridRenderCellParams) => {
    return params.row.feedbackType.type;
  }

  /*
 * Render name for row
 */
  const renderView = (params: GridCellParams) => {
    const id = Number(params.row.feedbackID);

    if (id > 0) {
      return (
        <Tooltip title={`View feedback detail`} arrow>
          <IconButton onClick={() => props.handleViewDetail(id)} size="large">
            <FeedbackIcon />
          </IconButton>
        </Tooltip>
      );
    } else {
      return (
        <span></span>
      );
    }

  }

  // #endregion

  // #region Event handlers
  // #endregion

  return (
    <DataGrid
      columns={columns()}
      rows={props.rows}
      getRowId={(row) => row.feedbackID}
      density="compact"
      disableColumnSelector
      disableRowSelectionOnClick
      hideFooter
      loading={props.isLoading}
      slots={{
        noRowsOverlay: noRowsOverlay
      }}
    />
  );

}

export default FeedbackGrid;